import type { Map as ImmutableMap } from 'immutable';
import { useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  createDefaultUsageFilter,
  getReportConfiguration,
} from 'app-components/flux/report-configurations/defaultReportConfigurations';
import type { ReportConfiguration } from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import type RootState from 'app-components/utils/redux-store/rootState';

export const useDefaultReportConfig = () => {
  const businessEnabled = useSelector((state: RootState) => entitlementsSelectors.getBusinessEnabled(state));
  const openBusinesses = useSelector(businessSelectors.getOpenBusinesses);
  const advancedReportingEnabled = useSelector((state: RootState) =>
    featureFlagsSelectors.getFeatureFlag(state, 'advancedReportingEnabled'),
  );
  const reportTaxDetailBusiness = useSelector((state: RootState) =>
    featureFlagsSelectors.getFeatureFlag(state, 'reportTaxDetailBusiness'),
  );
  const reportTaxDetailPersonal = useSelector((state: RootState) =>
    featureFlagsSelectors.getFeatureFlag(state, 'reportTaxDetailPersonal'),
  );
  const reportGridGraph = useSelector((state: RootState) =>
    featureFlagsSelectors.getFeatureFlag(state, 'reportGridGraph'),
  );
  const startYAxisAtZero = useSelector((state: RootState) =>
    featureFlagsSelectors.getFeatureFlag(state, 'startYAxisAtZero'),
  );
  const store = useStore();

  const getDefaultReportConfig = useCallback(
    (
      tabName: string,
      reportConfigId?: string,
      _options?: { isPersonalTaxes?: boolean },
    ): ReportConfiguration | null => {
      const localDefaultReportConfig = getReportConfiguration(tabName, true, startYAxisAtZero);
      // Read defaults from the store lazily instead of subscribing via useSelector,
      // so that changes to this map don't cause getDefaultReportConfig's identity to change
      // (which would cause an infinite loop through useEnsureReportForTab → useFocusEffect).
      const state = store.getState() as RootState & {
        reportConfigurationDefaultsStore?: { resourcesById?: ImmutableMap<string, ReportConfiguration> };
      };
      const resourcesById = state.reportConfigurationDefaultsStore?.resourcesById;
      const backendDefaultReportConfig = resourcesById?.find((rc) => rc.name === tabName);
      const defaultReportConfig = backendDefaultReportConfig ?? localDefaultReportConfig;

      if (!defaultReportConfig) {
        return null;
      }

      // Use the shared helper to create the default usage filter
      const filter = createDefaultUsageFilter(
        tabName,
        businessEnabled,
        startYAxisAtZero,
        openBusinesses.toArray(),
        Boolean(advancedReportingEnabled),
        Boolean(reportTaxDetailBusiness),
        Boolean(reportTaxDetailPersonal),
        Boolean(reportGridGraph),
      );

      return defaultReportConfig.merge({
        filter,
        id: reportConfigId,
        persistentType: 'TEMPORARY',
        clientId: defaultReportConfig.clientId || uuidv4().toUpperCase(),
      });
    },
    [
      store,
      businessEnabled,
      startYAxisAtZero,
      openBusinesses,
      advancedReportingEnabled,
      reportTaxDetailBusiness,
      reportTaxDetailPersonal,
      reportGridGraph,
    ],
  );

  return {
    getDefaultReportConfig,
  };
};
